import { Icon } from '@iconify/react';
import { Button, Divider, Input, Link } from '@nextui-org/react';
import React, { useEffect, useState } from 'react';

interface LoginFormProps {
  onSubmit: (email: string, password: string) => void;
  isLoading: boolean;
  loginWithGoogleSSO: () => void;
  isLoadingWithSSO: boolean;
  /** Incremented after each failed login to reset password field and block autofill swaps. */
  failedLoginAttempt?: number;
  /** Email from the last submit attempt; kept visible after a failed login. */
  lockedEmail?: string | null;
}

// Separate component for social login buttons
interface SocialButtonProps {
  icon: string;
  label: string;
  className?: string;
  onClick?: () => void;
  isLoading?: boolean;
}

const SocialButton = ({
  icon,
  label,
  className = '',
  onClick,
  isLoading,
}: SocialButtonProps) => (
  <Button
    startContent={<Icon icon={icon} width={20} height={20} />}
    radius="full"
    variant="bordered"
    className={`w-full border-1 text-xs md:text-sm h-10 md:h-12 ${className}`}
    onClick={onClick}
    isLoading={isLoading}
  >
    {label}
  </Button>
);
const LoginForm = ({
  onSubmit,
  isLoading,
  loginWithGoogleSSO,
  isLoadingWithSSO, // better make it object for all sso
  failedLoginAttempt = 0,
  lockedEmail = null,
}: LoginFormProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const isDisabled =
    formData.email.trim() === '' || formData.password.trim() === '';

  useEffect(() => {
    if (failedLoginAttempt <= 0 || !lockedEmail) {
      return;
    }

    setFormData({
      email: lockedEmail,
      password: '',
    });
  }, [failedLoginAttempt, lockedEmail]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData.email, formData.password);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((current) => ({
      ...current,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <form
      className="flex flex-col gap-4 md:gap-6"
      onSubmit={handleSubmit}
      autoComplete="on"
    >
      <Input
        label="Email Address"
        name="email"
        placeholder="janedoe@company.com"
        type="email"
        variant="bordered"
        autoComplete="username"
        value={formData.email}
        onChange={handleInputChange}
        classNames={{
          input: 'text-base',
          label: 'text-sm md:text-base',
        }}
        // className="border-none"
      />

      <Input
        key={`password-${failedLoginAttempt}`}
        label="Password"
        name="password"
        placeholder="••••••••"
        type={isVisible ? 'text' : 'password'}
        variant="bordered"
        autoComplete="current-password"
        value={formData.password}
        onChange={handleInputChange}
        classNames={{
          input: 'text-base',
          label: 'text-sm md:text-base',
        }}
        endContent={
          <button
            type="button"
            onClick={() => setIsVisible(!isVisible)}
            className="focus:outline-none"
          >
            <Icon
              className="text-xl md:text-2xl text-default-400"
              icon={isVisible ? 'solar:eye-closed-linear' : 'solar:eye-bold'}
            />
          </button>
        }
      />

      <div className="text-right">
        <Link
          href="/forgot-password"
          className="text-xs md:text-sm text-[#000]"
        >
          Forgot your password?
        </Link>
      </div>

      <Button
        color="primary"
        type="submit"
        size="lg"
        isLoading={isLoading}
        isDisabled={isDisabled}
        radius="full"
        className="w-full text-xs md:text-sm border-1 h-10 md:h-12"
      >
        Log in
      </Button>

      <div>
        <div className="flex w-full items-center gap-3 mb-6">
          <Divider className="flex-1" />
          <span className="text-xs md:text-sm text-gray-400 whitespace-nowrap">
            Or continue with
          </span>
          <Divider className="flex-1" />
        </div>
        <SocialButton
          icon="solar:key-linear"
          label="Single Sign-On"
          className="bg-white hover:bg-gray-50 border-gray-200"
          onClick={loginWithGoogleSSO}
          isLoading={isLoadingWithSSO}
        />
      </div>
    </form>
  );
};

export default LoginForm;
